import { useEffect, useState } from "react"
import useFetch from "../../../_fetch"
import { useAuth } from "../../../auth/auth-context"
import { ICustomAlert, IDuplicateConversions, IFeed_Alert } from "../../dashboard/alerts/alerts"
import { useAppContext } from "../../../AppContext"
import { format } from 'date-fns'
import { renderCustom, renderDuplicate, renderFeed } from "./render"
import { prepareChunks } from "./prepare-chunks"
import PageNumber from "../../dashboard/_page-number/page-number"
import { PageNumberProvider } from "../../dashboard/_page-number/page-number-context"

const isDev = process.env.NODE_ENV === 'development'
const reportBlue = '#5b9bd5'
const reportPageBackground = '#f8f9fd'
const reportPageStyle = {
    width: '210mm',
    height: '297mm',
    minHeight: '297mm',
    position: 'relative' as const,
    backgroundColor: reportPageBackground,
    boxSizing: 'border-box' as const,
    overflow: 'visible',
}

function ReportSectionHeading(opts: { title: string, action?: React.ReactNode, size?: 'default' | 'large' }) {
    const isLarge = opts.size === 'large'

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between gap-2">
                <h6 className="mb-2 fw-bold text-uppercase" style={{ color: reportBlue, fontSize: isLarge ? '1.45rem' : '0.95rem', letterSpacing: '0.02em' }}>
                    {opts.title}
                </h6>
                <div className="mb-2">{opts.action}</div>
            </div>
            <div style={{ height: 1, backgroundColor: 'rgba(91, 155, 213, 0.35)' }} />
        </div>
    )
}

function AlertReportHeader(opts: { title: string, dateRange?: string, imgPath: string }) {
    return (
        <div className="mb-3">
            <ReportSectionHeading
                title={opts.title}
                action={<img height={"38px"} src={opts.imgPath} alt="Logo" />}
                size="large"
            />
            {opts.dateRange ? (
                <div className="mt-2 mb-3 fw-semibold text-muted">
                    {opts.dateRange}
                </div>
            ) : null}
        </div>
    )
}

function ConditionalPageProvider({
  isIncluded,
  children,
}: {
  isIncluded: boolean
  children: React.ReactNode
}) {
  return !isIncluded ? <PageNumberProvider>{children}</PageNumberProvider> : <>{children}</>
}
export default function AlertReport(opts: { isIncluded?: boolean }) {
    const { doFetch } = useFetch()
    const [data, setData] = useState<{ duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] } | undefined>()
    const { user } = useAuth()
    const appContext = useAppContext() 
    const origin = isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'
    const imgPath = origin + '/logo' + (user?.username ? '/' + user?.username : '')
    const alertIds = appContext.alertIds || []

    const getFromDate = () => {
        if(appContext.fromDate) return new Date(appContext.fromDate)

        const fromDate = new Date()
        fromDate.setDate(fromDate.getDate() - 7)
        fromDate.setHours(0, 0, 0, 0)
        return fromDate
    }
    const getToDate = () => {
        const date = appContext.toDate ? new Date(appContext.toDate) : new Date()

        date.setHours(23, 59, 59, 999)
        return date
    }

    const fromDate = getFromDate()
    const toDate = getToDate()

    useEffect(() => {
        if (!appContext.isPdf) return

        const previousBodyBackground = document.body.style.backgroundColor
        const previousHtmlBackground = document.documentElement.style.backgroundColor

        document.body.style.backgroundColor = reportPageBackground
        document.documentElement.style.backgroundColor = reportPageBackground

        return () => {
            document.body.style.backgroundColor = previousBodyBackground
            document.documentElement.style.backgroundColor = previousHtmlBackground
        }
    }, [appContext.isPdf])

    const formatDateRange = (startDate: Date, endDate: Date): string => {
        return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    }

    const filteredDuplicates: IDuplicateConversions = {}
    if((!alertIds.length || alertIds.includes('duplicate')) && data) {
        for(const orderRef in data?.duplicateOrderRefs) {
            if(orderRef === '') continue
            const sortedConversions = [...data.duplicateOrderRefs[orderRef]].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            const newest = sortedConversions[sortedConversions.length - 1]
            const isInDateRange = newest && new Date(newest.date) >= fromDate && new Date(newest.date) <= toDate
            if(isInDateRange) filteredDuplicates[orderRef] = data.duplicateOrderRefs[orderRef]
        }
    }

    const filteredFeeds:IFeed_Alert[] = []
    if(!alertIds.length || alertIds.includes('feed')) {
        for(const feed of data?.feeds || []) {
            const feedDate = new Date(feed.date)
            if(feedDate >= fromDate && feedDate <= toDate) filteredFeeds.push(feed)
        }
    }
    

    const filteredCustom = (data?.customAlerts || []).filter((alert) => {
        const alertDate = new Date(alert.date)
        if(alertIds.length && !alertIds.includes(alert.id)) return false

        return alertDate >= fromDate && alertDate <= toDate
    })


    const chunks = prepareChunks(filteredDuplicates, filteredFeeds, filteredCustom)

    const getTotalAlerts = () => {
        let total = 0
        for(const chunk of chunks) {
            total += chunk.length
        }
        return total
    }
    ;(window as any).getTotalAlerts = () => {
        return getTotalAlerts()
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await doFetch("/alerts/get-alerts")
                const json: { duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] } = await response.json()
                setData(json)
            } catch (err) {
                console.error(err)
            }
        }
        fetchData()
    }, [])

    return (
        <ConditionalPageProvider isIncluded={!!opts.isIncluded}>
            <>
                {data && getTotalAlerts() === 0 ? (
                    <div className="px-3 pt-3" style={reportPageStyle}>
                        <PageNumber />
                        <AlertReportHeader title={appContext.reportName || 'Alert report'} dateRange={!opts.isIncluded ? formatDateRange(fromDate, toDate) : undefined} imgPath={imgPath} />
                        <ReportSectionHeading title="No alerts during this period" />
                        <p className="text-muted">
                            There have been no alerts in this date range.
                        </p>
                    </div>
                    
                ) : (
                    <div style={{ width: '210mm' }}>
                            {chunks.map((pageItems, pageIndex) => (
                                <div
                                    key={pageIndex}
                                    className={`${pageIndex === 0 ? '' : 'page-break'} px-3 pt-3`}
                                    style={reportPageStyle}
                                >
                                    <PageNumber />
                                    <AlertReportHeader title={appContext.reportName || 'Alert report'} dateRange={!opts.isIncluded ? formatDateRange(fromDate, toDate) : undefined} imgPath={imgPath} />
                                    {pageItems.map((item, idx) => {
                                        const prev = pageItems[idx-1]
                                        switch (item.type) {
                                            case 'duplicate': return renderDuplicate(item.data[0], item.data[1], user?.settings.currency || '', prev?.type !== 'duplicate')
                                            case 'feed': return renderFeed(item.data, `${pageIndex}-${idx}`, prev?.type !== 'feed')
                                            case 'custom': return renderCustom(item.data, `${pageIndex}-${idx}`, prev?.type !== 'custom', user?.settings.currency || '')
                                            default: return null
                                        }
                                    })}
                                </div>
                            ))}
                    </div>
                )}
                {data ? <div data-nordpar-pdf-ready="true" className="d-none" /> : ''}
            </>
        </ConditionalPageProvider>
    )
}
